import { useCallback, useEffect, useRef, useState } from 'react'
import type { AvailabilityResponse, ClubCoverage, Slot, SourceStatus } from '../../shared/types'
import { CLUBS } from '../../shared/clubs'
import { diffDays } from '../../shared/time'
import { planBatches } from '../lib/batches'

interface State {
  data: AvailabilityResponse | null
  loading: boolean
  error: string | null
}

/** Split batches produce one status per batch; fold them back per source. */
function mergeSources(statuses: SourceStatus[]): SourceStatus[] {
  const merged = new Map<string, SourceStatus>()
  for (const status of statuses) {
    const seen = merged.get(status.source)
    if (!seen) {
      merged.set(status.source, { ...status, clubIds: [...status.clubIds] })
      continue
    }
    seen.clubIds = [...new Set([...seen.clubIds, ...status.clubIds])]
    seen.cached = seen.cached && status.cached
    if (!status.ok) {
      seen.ok = false
      seen.error = seen.error ?? status.error
    }
  }
  return [...merged.values()]
}

/**
 * Fetches a week of availability as several parallel requests - one per source,
 * split further for sources with many clubs - because a single Worker
 * invocation is capped at 50 upstream subrequests.
 *
 * Results merge in as they land, so the grid fills progressively rather than
 * waiting on the slowest club. Weeks already seen this session render from
 * memory first and revalidate behind the scenes.
 */
export function useAvailability(from: string, to: string) {
  const cache = useRef(new Map<string, AvailabilityResponse>())
  const [state, setState] = useState<State>({ data: null, loading: true, error: null })
  const [nonce, setNonce] = useState(0)

  const refresh = useCallback(() => {
    cache.current.clear()
    setNonce((n) => n + 1)
  }, [])

  useEffect(() => {
    const key = `${from}:${to}`
    const cached = cache.current.get(key) ?? null
    const controller = new AbortController()
    const batches = planBatches(CLUBS, diffDays(from, to) + 1)

    setState({ data: cached, loading: batches.length > 0, error: null })

    if (batches.length === 0) {
      setState({
        data: {
          from,
          to,
          slots: [],
          sources: [],
          coverage: [],
          fetchedAt: new Date().toISOString(),
        },
        loading: false,
        error: null,
      })
      return
    }

    let slots: Slot[] = []
    let statuses: SourceStatus[] = []
    let coverage: ClubCoverage[] = []
    let settled = 0
    let failed = 0

    for (const batch of batches) {
      const params = new URLSearchParams({
        from,
        to,
        source: batch.source,
        clubs: batch.clubIds.join(','),
      })

      fetch(`/api/availability?${params}`, { signal: controller.signal })
        .then(async (res) => {
          if (!res.ok) throw new Error(`${batch.source} service returned ${res.status}`)
          return (await res.json()) as AvailabilityResponse
        })
        .then((res) => {
          slots = slots.concat(res.slots)
          statuses = statuses.concat(res.sources)
          coverage = coverage.concat(res.coverage ?? [])
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return
          failed++
          statuses = statuses.concat({
            source: batch.source,
            clubIds: batch.clubIds,
            ok: false,
            cached: false,
            error: err instanceof Error ? err.message : 'request failed',
          })
        })
        .finally(() => {
          if (controller.signal.aborted) return
          settled++
          const done = settled === batches.length
          const data: AvailabilityResponse = {
            from,
            to,
            slots: [...slots].sort((a, b) => a.startUtc.localeCompare(b.startUtc)),
            sources: mergeSources(statuses),
            coverage: [...coverage],
            fetchedAt: new Date().toISOString(),
          }
          // Only a complete week is worth replaying from memory.
          if (done && failed === 0) cache.current.set(key, data)
          setState({
            data,
            loading: !done,
            error: done && failed === batches.length ? 'Could not load availability' : null,
          })
        })
    }

    return () => controller.abort()
  }, [from, to, nonce])

  return { ...state, refresh }
}
